import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAppState } from "@/store/useApp";
import { WizardData } from "./AssessmentWizard";
import { cn } from "@/lib/utils";
import { AssessmentType } from "@/types/assessment";
import { Separator } from "@/components/ui/separator";
import { AgentAvatar } from "@/components/AgentAvatar";

interface Step1DetailsProps {
  data: WizardData;
  updateData: (updates: Partial<WizardData>) => void;
}

export const Step1Details = ({ data, updateData }: Step1DetailsProps) => {
  const agents = useAppState((state) => state.agents || []);

  const filteredAgents = agents.filter((agent) => {
    if (data.type === "hybrid") return agent.type === "hybrid";
    if (data.type === "oral") return agent.type === "standalone";
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          value={data.title}
          onChange={(e) => updateData({ title: e.target.value })}
          placeholder="e.g. Startup Ideation Workshop"
        />
      </div>
      <Separator/>

      <div>
        <Label>Assessment type</Label>
        <RadioGroup
        className="mt-4"
          value={data.type}
          onValueChange={(val) => updateData({ type: val as AssessmentType })}
        >
          <div className="flex items-start space-x-3 px-4">
            <RadioGroupItem value="written" id="written" className="mt-1" />
            <div className="space-y-1">
              <Label htmlFor="written" className="font-medium cursor-pointer">
                Written assessment
              </Label>
              <div className="text-sm text-muted-foreground">
                Contains only the student submission.
              </div>
            </div>
          </div>
          <div className="flex items-start space-x-3 px-4">
            <RadioGroupItem value="hybrid" id="hybrid" className="mt-1" />
            <div className="space-y-1">
              <Label htmlFor="hybrid" className="font-medium cursor-pointer">
                Hybrid assessment
              </Label>
              <div className="text-sm text-muted-foreground">
                Contains student submission and agentic assessment.
              </div>
            </div>
          </div>
          <div className="flex items-start space-x-3 px-4">
            <RadioGroupItem value="oral" id="oral" className="mt-1" />
            <div className="space-y-1">
              <Label htmlFor="oral" className="font-medium cursor-pointer">
                Oral assessment
              </Label>
              <div className="text-sm text-muted-foreground">
                Contains only the agentic assessment.
              </div>
            </div>
          </div>
        </RadioGroup>
      </div>

      {(data.type === "hybrid" || data.type === "oral") && (
        <div className="space-y-4">
          <Label>Select agent</Label>
          <RadioGroup
            value={data.agentId}
            onValueChange={(val) => updateData({ agentId: val })}
            className="grid gap-4"
          >
            {filteredAgents.map((agent) => (
              <div
                key={agent.id}
                className={cn(
                  "flex items-center justify-between space-x-3 space-y-0 rounded-md border p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary",
                  data.agentId === agent.id && "border-primary"
                )}
              >
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value={agent.id} id={agent.id} />
                  <div>
                    <Label
                      htmlFor={agent.id}
                      className="font-medium cursor-pointer"
                    >
                      {agent.name}
                    </Label>
                    {agent.description && (
                      <div className="text-sm text-muted-foreground line-clamp-1">
                        {agent.description}
                      </div>
                    )}
                  </div>
                </div>
                <AgentAvatar agent={agent} size="sm" />
              </div>
            ))}
            {filteredAgents.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No agents available for this assessment type. Please create one first.
              </div>
            )}
          </RadioGroup>
        </div>
      )}
    </div>
  );
};
